import {
  GetProgramEnrollmentPaymentPlansDocument,
  GetViewerLearningDetailsDocument,
  PaymentPlanStatus,
  ProgramEnrollmentStatus,
} from '@sandtech-hub-monorepo/ehub-client';
import { useQuery } from '@apollo/client';
import { useCognito } from '@sandtech-hub-monorepo/ui-auth-kit';
import { LearningSkeleton } from '../components/LearningSkeleton';
import { ProgramEnrollmentsEmptyState } from './components/ProgramEnrollmentsEmptyState';
import { useMemo } from 'react';
import { ProgramEnrollmentView } from './components/ProgramEnrollmentView';
import { EnrollmentPausedNotice } from '../components/EnrollmentPausedNotice';
import { AlxProgramEnrollmentPaymentBanner } from '../components/AlxProgramEnrollmentPaymentBanner';

type ProgramEnrollmentsViewProps = {
  type: 'current' | 'past' | 'graduated';
  paymentFAQsLink: string;
  onlyOngoingCourses?: boolean;
  showOngoingCoursesToggle?: boolean;
};

export const ProgramEnrollmentsView = ({
  type,
  paymentFAQsLink,
  onlyOngoingCourses = false,
  showOngoingCoursesToggle = false,
}: ProgramEnrollmentsViewProps) => {
  const { currentUser, loading: authLoading } = useCognito();

  const { data, loading } = useQuery(GetViewerLearningDetailsDocument, {
    fetchPolicy: 'cache-first',
    skip: !currentUser,
  });
  const { data: paymentPlansData, loading: paymentPlansLoading } = useQuery(
    GetProgramEnrollmentPaymentPlansDocument,
    {
      fetchPolicy: 'cache-first',
      skip: !currentUser,
    }
  );

  const {
    ongoingProgramEnrollments,
    pausedProgramEnrollments,
    pastProgramEnrollments,
    graduatedProgramEnrollments,
  } = useMemo(() => {
    const enrollments = data?.viewer.programEnrollments || [];
    const pastStatuses = [
      ProgramEnrollmentStatus.Graduated,
      ProgramEnrollmentStatus.Failed,
    ];
    // A paused enrollment is neither ongoing nor past, so before this bucket
    // existed it fell out of both lists and the learner saw the empty state
    // instead of their own program. It is kept separate from the ongoing list
    // rather than merged into it because the two render differently: per the
    // designs a paused learner sees a notice in place of their coursework, and
    // the payment banners below are deliberately tied to ONGOING only.
    const pausedStatuses = [
      ProgramEnrollmentStatus.LeaveOfAbsence,
      ProgramEnrollmentStatus.Deferred,
    ];

    return {
      ongoingProgramEnrollments: enrollments.filter(
        ({ status }) => status === ProgramEnrollmentStatus.Ongoing
      ),
      pausedProgramEnrollments: enrollments.filter(({ status }) =>
        pausedStatuses.includes(status)
      ),
      // A cancelled university enrollment never happened as far as the learner
      // is concerned: a rejected or cancelled admission application voids its
      // PENDING enrollment as FAILED + cancelled, and showing that here as
      // "Withdrawn" under past programs would tell the learner they withdrew
      // from a programme they were never in. Scoped to university programs so
      // the ALX past-programs list is unchanged.
      pastProgramEnrollments: enrollments.filter(
        ({ status, cancelled, program }) =>
          pastStatuses.includes(status) &&
          !(cancelled && program.isUniversityProgram)
      ),
      graduatedProgramEnrollments: enrollments.filter(
        ({ status }) => status === ProgramEnrollmentStatus.Graduated
      ),
    };
  }, [data?.viewer.programEnrollments]);

  const hasOngoingProgramEnrollments = ongoingProgramEnrollments.length > 0;
  const hasPausedProgramEnrollments = pausedProgramEnrollments.length > 0;
  const hasPastProgramEnrollments = pastProgramEnrollments.length > 0;
  const hasGraduatedProgramEnrollments = graduatedProgramEnrollments.length > 0;

  if (loading || authLoading || paymentPlansLoading) {
    return <LearningSkeleton />;
  }

  return (
    <>
      {!hasOngoingProgramEnrollments &&
        !hasPausedProgramEnrollments &&
        type === 'current' && <ProgramEnrollmentsEmptyState />}
      {/* A paused enrollment shows the notice instead of its coursework: the
          learner has no courses to act on while their enrollment is on hold. */}
      {hasPausedProgramEnrollments &&
        type === 'current' &&
        pausedProgramEnrollments.map((enrollment) => {
          const activeLeaveOfAbsence = enrollment.leavesOfAbsence?.find(
            (leave) => !leave.endedAt
          );
          const activeDeferral = enrollment.deferrals?.find(
            (deferral) => !deferral.endedAt
          );
          const resumesOn =
            activeLeaveOfAbsence?.expectedReturnDate ??
            activeDeferral?.expectedReturnDate;

          // The status says paused, so a row should always exist. If one is
          // missing the status is the source of truth, so still explain the
          // pause rather than dropping the program off the page again.
          return (
            <EnrollmentPausedNotice
              key={enrollment.id}
              kind={
                enrollment.status === ProgramEnrollmentStatus.LeaveOfAbsence
                  ? 'leaveOfAbsence'
                  : 'deferral'
              }
              resumesOn={resumesOn}
            />
          );
        })}
      {hasOngoingProgramEnrollments && type === 'current' && (
        <>
          {paymentPlansData?.viewer?.paymentPlans
            ?.filter(({ status }) => status !== PaymentPlanStatus.Cancelled)
            .map((paymentPlan) => (
              <AlxProgramEnrollmentPaymentBanner
                key={paymentPlan.paymentPlanId}
                paymentPlan={paymentPlan}
                paymentFAQsLink={paymentFAQsLink}
              />
            ))}
          {paymentPlansData?.viewer?.paymentPlans.length === 0 &&
            ongoingProgramEnrollments.find(
              ({ program }) => program.allAccessPaymentPlan
            ) && (
              <AlxProgramEnrollmentPaymentBanner
                paymentFAQsLink={paymentFAQsLink}
              />
            )}
          {ongoingProgramEnrollments.map((enrollment) => (
            <ProgramEnrollmentView
              enrollment={enrollment}
              key={enrollment.id}
              onlyOngoingCourses={onlyOngoingCourses}
              showOngoingCoursesToggle={showOngoingCoursesToggle}
            />
          ))}
        </>
      )}
      {hasPastProgramEnrollments &&
        type === 'past' &&
        pastProgramEnrollments.map((enrollment) => (
          <ProgramEnrollmentView enrollment={enrollment} key={enrollment.id} />
        ))}
      {!hasPastProgramEnrollments && type === 'past' && (
        <div className="px-12 py-6 gap-1 flex flex-col items-center border border-border rounded-2xl">
          <span className="text-xl font-bold">
            You have no past programs yet
          </span>
        </div>
      )}
      {hasGraduatedProgramEnrollments &&
        type === 'graduated' &&
        graduatedProgramEnrollments.map((enrollment) => (
          <ProgramEnrollmentView
            enrollment={enrollment}
            key={enrollment.id}
            coursesHiddenByDefault
          />
        ))}
    </>
  );
};
